import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, CheckCircle2, Truck, MapPin, Phone, WifiOff, Cloud } from "lucide-react";
import api, { fileUrl } from "@/api";
import TopBar from "@/components/TopBar";
import { StatusPill } from "@/components/Pills";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { queueDelivery, queueSize, initOfflineSync } from "@/lib/offlineQueue";

// Phase 13: UNIT-ONLY mode. No payment method, no amount, no ₹ anywhere.
export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, displayName } = useLang();
  const [order, setOrder] = useState(null);
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [geo, setGeo] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(queueSize());
  const fileRef = useRef();

  useEffect(() => {
    initOfflineSync((size) => setPending(size));
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    return () => { window.removeEventListener("online", onUp); window.removeEventListener("offline", onDown); };
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const load = async () => {
    const r = await api.get(`/orders/${id}`);
    setOrder(r.data);
  };

  const startDelivery = async () => {
    await api.post(`/orders/${id}/start-delivery`);
    toast.success(t("start_delivery"));
    load();
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => {},
        { timeout: 8000, enableHighAccuracy: false }
      );
    }
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(r.data.path);
      setPhotoPreview(URL.createObjectURL(f));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };

  const deliver = async () => {
    if (!photoPath) return toast.error(t("photo_proof"));
    setBusy(true);
    const payload = { photo_path: photoPath, geo: geo || undefined };
    try {
      if (!navigator.onLine) {
        queueDelivery(id, payload);
        setPending(queueSize());
        toast.success("Saved offline — will sync when online");
        nav("/salesman");
        return;
      }
      await api.post(`/orders/${id}/deliver`, payload);
      toast.success(t("delivery_done"));
      nav("/salesman");
    } catch (e) {
      if (!e?.response) {
        queueDelivery(id, payload);
        setPending(queueSize());
        toast.success("Saved offline — will sync when online");
        nav("/salesman");
      } else {
        toast.error(e?.response?.data?.detail || "Error");
      }
    } finally { setBusy(false); }
  };

  if (!order) return <div className="min-h-screen grid place-items-center text-[#86868B]">{t("loading")}</div>;

  const isDelivered = order.status === "delivered";
  const totalUnits = order.total_units || (order.items || []).reduce((s, it) => s + (it.quantity || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10">
      <TopBar title={order.shop_name} onBack={() => nav("/salesman")} />
      {(!online || pending > 0) && (
        <div className={`px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-2 ${!online ? "bg-amber-100 text-amber-900" : "bg-blue-100 text-blue-900"}`} data-testid="offline-banner">
          {!online ? <><WifiOff className="w-4 h-4" /> {t("offline")} • Photos & deliveries will sync later</>
                  : <><Cloud className="w-4 h-4" /> {pending} {t("pending_sync")}</>}
        </div>
      )}
      <div className="max-w-2xl mx-auto p-4 sm:p-6 fade-up space-y-5">
        <div className="bg-white rounded-3xl p-5 soft-shadow">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-[#86868B] uppercase tracking-wider">{t("units_sold")}</div>
              <div className="heading-font text-3xl font-bold text-[#1D1D1F]" data-testid="order-total-units">{totalUnits} {t("units")}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <StatusPill status={order.status} t={t} />
              {order.priority && <span className="pill bg-red-50 text-red-700">⚡ {t("high_priority")}</span>}
            </div>
          </div>
          {order.shop?.address && (
            <div className="flex items-center gap-2 text-sm text-[#86868B] mb-1">
              <MapPin className="w-4 h-4" /> {order.shop.address}
            </div>
          )}
          {order.shop?.phone && (
            <a href={`tel:${order.shop.phone}`} className="flex items-center gap-2 text-sm text-[#0055FF] font-semibold">
              <Phone className="w-4 h-4" /> {order.shop.phone}
            </a>
          )}
        </div>

        <div className="bg-white rounded-3xl p-5 soft-shadow">
          <h3 className="heading-font font-bold text-lg mb-3">{t("items")}</h3>
          <div className="space-y-2">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                <div>
                  <div className="font-semibold">{displayName(it)}</div>
                </div>
                <div className="font-bold heading-font">×{it.quantity}</div>
              </div>
            ))}
          </div>
        </div>

        {!isDelivered && order.status === "assigned" && (
          <button
            data-testid="start-delivery-btn"
            onClick={startDelivery}
            className="w-full bg-[#FF9500] hover:bg-[#E68500] text-white rounded-2xl py-4 text-lg font-bold flex items-center justify-center gap-2 shadow-md"
          >
            <Truck className="w-5 h-5" /> {t("start_delivery")}
          </button>
        )}

        {!isDelivered && order.status === "out_for_delivery" && (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl p-5 soft-shadow">
              <h3 className="heading-font font-bold text-lg mb-3">{t("photo_proof")}</h3>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid="photo-input" />
              {!photoPreview ? (
                <button onClick={() => fileRef.current?.click()} data-testid="take-photo-btn" className="w-full btn-massive py-8 border-dashed border-2 border-[#0055FF]/30">
                  <Camera className="w-12 h-12 text-[#0055FF]" />
                  <span className="font-bold heading-font">{t("take_photo")}</span>
                </button>
              ) : (
                <div className="relative">
                  <img src={photoPreview} alt="proof" className="w-full rounded-2xl object-cover max-h-72" data-testid="photo-preview" />
                  <button onClick={() => fileRef.current?.click()} className="absolute bottom-2 right-2 bg-white/95 px-3 py-1.5 rounded-xl text-sm font-bold shadow">
                    Retake
                  </button>
                </div>
              )}
              {geo && (
                <div className="mt-3 text-xs text-[#86868B] flex items-center gap-1.5" data-testid="geo-tag">
                  <MapPin className="w-3.5 h-3.5 text-[#34C759]" />
                  Geo-tagged: {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)} {geo.accuracy && `(~${Math.round(geo.accuracy)}m)`}
                </div>
              )}
            </div>

            <button
              onClick={deliver}
              disabled={busy || !photoPath}
              data-testid="mark-delivered-btn"
              className="w-full bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl py-4 text-lg font-bold flex items-center justify-center gap-2 shadow-md"
            >
              <CheckCircle2 className="w-5 h-5" /> {t("mark_delivered")}
            </button>
          </div>
        )}

        {isDelivered && (
          <div className="bg-green-50 border border-green-200 rounded-3xl p-5 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#34C759] mx-auto mb-2" />
            <div className="heading-font font-bold text-lg text-[#1D1D1F]">{t("delivery_done")}</div>
            <div className="text-sm text-[#86868B]">{totalUnits} {t("units")}</div>
            {order.photo_path && (
              <img src={fileUrl(order.photo_path)} alt="proof" className="mt-3 mx-auto max-h-48 rounded-xl" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
